/**
 * Bedragen zijn in de hele app **hele centen**. Het pakket levert ze als
 * decimale string met twee decimalen (`"1450.00"`); die wordt cijfer voor
 * cijfer omgerekend, nooit via een float. Zo tellen client en server tot op de
 * cent hetzelfde op.
 */

const AMOUNT_PATTERN = /^([+-]?)(\d*)(?:[.,](\d*))?$/;

/**
 * `"1450.00"` -> `145000`. Leeg, `null` of onleesbaar telt als `0` — dat is de
 * typenormalisatie die de legacy data afdwingt (`data-quality.md` §10).
 * Meer dan twee decimalen worden afgekapt, niet afgerond.
 */
export function parseAmountToCents(value: string | number | null | undefined): number {
    if (value === null || value === undefined) {
        return 0;
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? Math.round(value * 100) : 0;
    }

    const trimmed = value.trim();

    if (trimmed === '') {
        return 0;
    }

    const match = AMOUNT_PATTERN.exec(trimmed);

    if (!match) {
        return 0;
    }

    const whole = match[2] === '' || match[2] === undefined ? '0' : match[2];
    const fraction = (match[3] ?? '').padEnd(2, '0').slice(0, 2);
    const cents = Number(whole) * 100 + Number(fraction);

    return match[1] === '-' ? -cents : cents;
}

/** Wat PHP `is_numeric()` als getal leest, na trimmen. */
const NUMERIC_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;

/**
 * Een eigen bedrag van de inspecteur in centen, of `null` als er geen is.
 * Anders dan `parseAmountToCents` is leeg hier **niet** 0: `null`, `""` en
 * onleesbaar betekenen "niet aangepast", `"0.00"` is een aanpassing naar nul.
 * Leest exact zoals `Amount::toCents()` in de API, die dezelfde kolom leest.
 */
export function parseAmountOverrideToCents(
    value: string | number | null | undefined,
): number | null {
    if (value === null || value === undefined) {
        return null;
    }

    const trimmed = typeof value === 'number' ? value : value.trim();
    const amount =
        typeof trimmed === 'number'
            ? trimmed
            : NUMERIC_PATTERN.test(trimmed)
              ? Number(trimmed)
              : Number.NaN;

    return Number.isFinite(amount) ? roundHalfAwayFromZero(amount * 100) : null;
}

/**
 * De precisie van een hoeveelheid: `inspection_answers.value_number` is een
 * `decimal(12,3)`, dus de server bewaart duizendsten en niet meer.
 */
const QUANTITY_SCALE = 1000;

/**
 * Een bedrag in centen maal een hoeveelheid, afgerond op hele centen: half naar
 * boven, bij een negatief bedrag van nul af. Exact `Quantity::times()` in de API.
 *
 * De hoeveelheid wordt één keer naar hele duizendsten afgerond; daarna is alles
 * gehele rekenkunde. Zo komt 2,05 m × € 19,90 uit op € 40,80 en niet — via een
 * float die net onder € 40,795 ligt — op € 40,79. Het hele deel en het deel
 * achter de komma worden apart vermenigvuldigd, zodat geen tussenresultaat
 * groter wordt dan de uitkomst zelf.
 */
export function multiplyCents(cents: number, quantity: number): number {
    const thousandths = roundHalfAwayFromZero(quantity * QUANTITY_SCALE);
    const fraction = thousandths % QUANTITY_SCALE;
    const whole = (thousandths - fraction) / QUANTITY_SCALE;
    const part = cents * fraction;
    const shifted = Math.abs(part) + QUANTITY_SCALE / 2;
    const rounded = (shifted - (shifted % QUANTITY_SCALE)) / QUANTITY_SCALE;
    const total = cents * whole + (part < 0 ? -rounded : rounded);

    /* Nooit -0: dat is voor `Object.is` en dus voor de tests een ander getal. */
    return total === 0 ? 0 : total;
}

/** Zoals PHP `round()`: half van nul af. Nooit -0. */
function roundHalfAwayFromZero(value: number): number {
    const rounded = value < 0 ? -Math.round(-value) : Math.round(value);

    return rounded === 0 ? 0 : rounded;
}

/** `145000` -> `"1450.00"`. */
export function formatCentsToAmount(cents: number): string {
    const sign = cents < 0 ? '-' : '';
    const absolute = Math.abs(Math.trunc(cents));
    const whole = Math.trunc(absolute / 100);
    const fraction = absolute - whole * 100;

    return `${sign}${whole}.${String(fraction).padStart(2, '0')}`;
}
